import { useState } from 'react';
import { toast } from 'sonner';
import { useCan } from '@discovr/common';
import { useCampaignCapabilitiesStore, useOutreachHandleImport } from '@discovr/features';
import { SCOPES, type OutreachHandlePreview, type OutreachImportBatch } from '@discovr/types';
import { canWriteAtLevel } from '../lib/campaignPermissions';
import { useOutreachSubParam } from '../campaign-detail/useOutreachSubParam';
import { POOL_COPY } from './constants/findCopy';
import { errorText } from './discoveryErrors';
import { plural } from './discoveryText';
import { outreachHandles, type PoolCreator } from './poolModel';

const COPY = POOL_COPY.outreach;

export function previewSummary(preview: OutreachHandlePreview): { title: string; body: string } {
  const { counts } = preview;
  const adding = plural(counts.ok, 'creator');
  const cantAdd = counts.invalid + counts.duplicate + counts.hasPrice + counts.wrongPlatform;
  const leftOut = [
    ...(counts.alreadyInCampaign ? [COPY.alreadyIn(counts.alreadyInCampaign)] : []),
    ...(cantAdd ? [COPY.cantAdd(cantAdd)] : []),
  ];
  return { title: COPY.title(adding), body: COPY.body(adding, leftOut) };
}

const ADDED_COPY = {
  added: (creators: string) => `${creators} added to outreach`,
  alreadyIn: (creators: string) => `${creators} already in outreach`,
  failed: 'Adding to outreach failed. Try again.',
};

export type PoolOutreachWording = 'added' | 'sent';

/** The toast follows what the commit actually did: new rows, rows that matched an existing creator, or both. Null when nothing happened. */
export function importOutcomeText(batch: Pick<OutreachImportBatch, 'createdCount' | 'matchedCount'>, wording: PoolOutreachWording): string | null {
  const { createdCount, matchedCount } = batch;
  const created = createdCount > 0 ? (wording === 'sent' ? COPY.sent : ADDED_COPY.added)(plural(createdCount, 'creator')) : null;
  const matched = matchedCount > 0 ? ADDED_COPY.alreadyIn(plural(matchedCount, 'creator')) : null;
  if (created && matched) return `${ADDED_COPY.added(plural(createdCount, 'creator'))}, ${plural(matchedCount, 'creator')} already in outreach`;
  return created ?? matched;
}

/** Pool's "Send to outreach": preview the selected handles, confirm, then start the outreach import. */
export function usePoolOutreach(campaignId: string, onSent: () => void, wording: PoolOutreachWording = 'added') {
  const { setSub } = useOutreachSubParam();
  const { preview, commit } = useOutreachHandleImport(campaignId, (batch) => {
    if (batch.status === 'failed') {
      toast.error(ADDED_COPY.failed);
      return;
    }
    const text = importOutcomeText(batch, wording);
    if (!text) {
      toast.info(COPY.nothingNew);
      return;
    }
    toast.success(text, { action: { label: COPY.open, onClick: () => setSub('inbox') } });
  });
  // Mirrors the Outreach tab's own gate: the role may write outreach, and the campaign grant allows it here.
  const accessLevel = useCampaignCapabilitiesStore((s) => s.accessLevel);
  const canSend = useCan(SCOPES.outreachWrite) && canWriteAtLevel(accessLevel);
  const [previewed, setPreviewed] = useState<OutreachHandlePreview | null>(null);

  const blockedReason = (creators: readonly PoolCreator[]): string | null => {
    if (!canSend) return COPY.noAccess;
    return outreachHandles(creators).length ? null : COPY.instagramOnly;
  };

  const start = (creators: readonly PoolCreator[]) => {
    preview.mutate(outreachHandles(creators), {
      onSuccess: (result) => {
        if (result.counts.ok) setPreviewed(result);
        else toast.info(COPY.nothingNew);
      },
      onError: (error) => toast.error(errorText(error)),
    });
  };

  const confirm = () => {
    if (!previewed) return;
    commit.mutate(previewed, {
      onSuccess: () => {
        setPreviewed(null);
        onSent();
      },
      onError: (error) => toast.error(errorText(error)),
    });
  };

  return {
    blockedReason,
    start,
    confirm,
    cancel: () => setPreviewed(null),
    previewed,
    previewing: preview.isPending,
    committing: commit.isPending,
  };
}
